import { insertImage } from '@platejs/media';
import { ImagePlugin } from '@platejs/media/react';
import { createPlatePlugin } from 'platejs/react';

export const DropPlugin = createPlatePlugin({
  key: 'drop',
  options: {
    isUploadingInlineImages: false,
    allowedImageTypes: [] as { type: string; extension: string }[],
    allowPublicImageUpload: undefined as
      | 'required'
      | 'allowed'
      | 'disallowed'
      | undefined,
    pendingUploadCallback: undefined as
      | ((uploadAsPubliclyAccessible: boolean) => void)
      | undefined,
  },
  handlers: {
    onDrop: ({ editor, event, setOption, getOptions }) => {
      try {
        const files = event.dataTransfer?.files;
        if (!files) return;

        const { allowedImageTypes } = getOptions();

        const allImageFiles = Array.from(files).filter((file) =>
          file.type.startsWith('image/'),
        );
        if (allImageFiles.length === 0) return;

        const imageFiles = allImageFiles.filter((file) => {
          if (allowedImageTypes.length === 0) return true;

          return allowedImageTypes.some(
            (allowed) => allowed.type === file.type,
          );
        });

        event.preventDefault();

        if (imageFiles.length === 0) return true;

        const { allowPublicImageUpload } = getOptions();

        // When not 'disallowed', show to the accessibility modal
        if (allowPublicImageUpload !== 'disallowed') {
          setOption('pendingUploadCallback', (isPublic: boolean) => {
            const { onUploadImage } = editor.getOptions(ImagePlugin) as {
              onUploadImage?: (
                fileOrData: File | string,
                opt: { uploadAsPubliclyAccessible?: boolean },
              ) => Promise<string>;
            };

            // Since the plugin handler is synchronous, the upload must operate as "fire-and-forget".
            // However, a dedicated loading spinner visualizes the process for the user and prevents concurrent changes.
            void (async () => {
              setOption('isUploadingInlineImages', true);
              try {
                await Promise.all(
                  imageFiles.map(async (file) => {
                    if (onUploadImage) {
                      try {
                        const uploadedUrl = await onUploadImage(file, {
                          uploadAsPubliclyAccessible: isPublic,
                        });
                        insertImage(editor, uploadedUrl);
                      } catch {
                        // Upload failed, skip image.
                      }
                    }
                  }),
                );
              } finally {
                setOption('isUploadingInlineImages', false);
              }
            })();
          });
          return true;
        }

        const { onUploadImage } = editor.getOptions(ImagePlugin) as {
          onUploadImage?: (
            fileOrData: File | string,
            opt: { uploadAsPubliclyAccessible?: boolean },
          ) => Promise<string>;
        };

        // Since the plugin handler is synchronous, the upload must operate as "fire-and-forget".
        // However, a dedicated loading spinner visualizes the process for the user and prevents concurrent changes.
        void (async () => {
          setOption('isUploadingInlineImages', true);
          try {
            await Promise.all(
              imageFiles.map(async (file) => {
                if (onUploadImage) {
                  try {
                    const uploadedUrl = await onUploadImage(file, {
                      uploadAsPubliclyAccessible: false,
                    });
                    insertImage(editor, uploadedUrl);
                  } catch {
                    // Upload failed, skip image.
                  }
                }
              }),
            );
          } finally {
            setOption('isUploadingInlineImages', false);
          }
        })();

        return true;
      } catch {
        // ignore
      }
    },
  },
});
